/**
 * NotionRenderer — 데이터베이스형
 */
import React from 'react';
import { Trans, t } from '@lingui/macro';
import { cn } from '../../../lib/utils';
import { Pin } from 'lucide-react';
import { StyleRendererProps, timeAgo, CommentBadge, NotifDot } from './shared';
import { UserAvatar } from '../../components/UserAvatar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';

export const NotionRenderer: React.FC<StyleRendererProps> = ({
  posts, onPostClick, selectedPostId, unreadPostIds, notifiedPostIds,
}) => (
  <div className="flex flex-col h-full">
    <div className="flex-1 overflow-y-auto px-2 lg:px-5">
      <table className="w-full">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-border text-meta-sm font-semibold text-muted-foreground">
            <th className="text-left px-5 py-2.5 bg-muted/40 backdrop-blur-sm"><Trans>Title</Trans></th>
            <th className="text-left px-3 py-2.5 w-[100px] bg-muted/40 backdrop-blur-sm"><Trans>Author</Trans></th>
            <th className="text-left px-3 py-2.5 w-[100px] bg-muted/40 backdrop-blur-sm"><Trans>Tags</Trans></th>
            <th className="text-left px-3 py-2.5 w-20 bg-muted/40 backdrop-blur-sm"><Trans>Date</Trans></th>
            <th className="text-center px-3 py-2.5 w-16 whitespace-nowrap bg-muted/40 backdrop-blur-sm"><Trans>Comments</Trans></th>
            <th className="text-center px-3 py-2.5 w-16 whitespace-nowrap bg-muted/40 backdrop-blur-sm"><Trans>Views</Trans></th>
          </tr>
        </thead>
        <tbody>
          {posts.map(post => (
            <tr
              key={post.id}
              onClick={() => onPostClick(post)}
              className={cn(
                'border-b border-border/30 cursor-pointer transition-colors',
                selectedPostId === post.id ? 'bg-accent' : 'hover:bg-muted/40',
              )}
            >
              <td className="px-5 py-2.5">
                <div className="flex items-center gap-2">
                  {post.is_pinned && (
                    <span className="text-meta-sm bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium shrink-0">
                      <Trans>Pinned</Trans>
                    </span>
                  )}
                  <span className="text-label text-foreground/90 truncate">{post.title}</span>
                  {notifiedPostIds?.has(post.id) && <NotifDot />}
                </div>
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <UserAvatar src={post.author.photoUrl ?? null} name={post.author.name} className="size-7 shrink-0" />
                  <span className="text-xs text-muted-foreground truncate">{post.author.name}</span>
                </div>
              </td>
              <td className="px-3 py-2.5">
                <NotionTagsCell tags={post.tags ?? []} />
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">{timeAgo(post.created_at)}</td>
              <td className="px-3 py-2.5 text-center text-xs text-muted-foreground tabular-nums">
                <span className="relative inline-flex items-center">
                  {post.comment_count}
                  {unreadPostIds?.has(post.id) && post.comment_count > 0 && (
                    <span className="absolute -top-0.5 -right-2 size-1.5 rounded-full bg-primary animate-pulse" />
                  )}
                </span>
              </td>
              <td className="px-3 py-2.5 text-center text-xs text-muted-foreground tabular-nums">{post.view_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/** 테이블 태그 셀 — 1개면 단일 chip, 2개 이상이면 첫 chip + 겹쳐진 +N chip → 클릭 popover 로 전체 표시.
 *  row click(게시글 상세) 전파 방지를 위해 button 에 e.stopPropagation. */
const NotionTagsCell: React.FC<{ tags: string[] }> = ({ tags }) => {
  if (tags.length === 0) return null;

  const first = tags[0];
  const extra = tags.length - 1;

  if (extra === 0) {
    return (
      <span className="inline-flex text-meta-sm px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
        {first}
      </span>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          title={t`View ${tags.length} tags`}
        >
          <span className="text-meta-sm px-1.5 py-0.5 rounded bg-muted text-muted-foreground relative z-10 max-w-[80px] truncate">
            {first}
          </span>
          {/* 뒤에 살짝 겹쳐진 +N chip — 시각적으로 stacked */}
          <span className="text-meta-sm px-1.5 py-0.5 rounded bg-muted text-muted-foreground/80 ml-[-6px] border-l border-background tabular-nums">
            +{extra}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto max-w-[280px] p-2"
        side="bottom"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider px-1 pb-1.5 mb-1 border-b border-border/50">
          <Trans>Tags {tags.length}</Trans>
        </div>
        <div className="flex flex-wrap gap-1">
          {tags.map(t => (
            <span key={t} className="text-meta-sm px-2 py-0.5 rounded bg-muted text-muted-foreground">
              {t}
            </span>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
